/**
 * SideSubVideoList: SIDE 모드 하단/우측 수평 스크롤 미니 영상 목록.
 *
 * v1 원본: RenewalComponents/SVContent/SideScreen/SubVideoList/
 * v2 변환: 단일 함수형 컴포넌트
 *
 * - 메인 참여자를 제외한 나머지 참여자들을 수평 스크롤로 표시
 * - 정렬 기준(AUTO/SPEAKER/NAME) 적용, 최대 8명까지 표시
 * - 클릭 → 해당 참여자를 메인으로 설정
 */
import React, { useRef, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import VideoPanel from '../SubVideo/VideoPanel';
import type { Participant } from '@/store/slices/participantsSlice';
import type { ScreenUser } from '@/types/components';
import { isRecordingBot } from '@/utils/recordingBot';
import { isRecorderMode } from '@/utils/recorderMode';

interface SideSubVideoListProps {
  localUserId: string;
  mainUserId: string | null;
}

const MAX_SIDE_SUB_COUNT = 100; // 최대 표시 인원 (화면 공유 포함 8명 → 50명으로 확대)

const SideSubVideoList: React.FC<SideSubVideoListProps> = ({ localUserId, mainUserId }) => {
  const dispatch = useAppDispatch();
  const participants = useAppSelector((s) => s.participants);
  const screenShare = useAppSelector((s) => s.screenShare);
  const screenSort = useAppSelector((s) => s.ui.screenSort);
  const speakerOrder = useAppSelector((s) => s.audioIndicator.speakerOrder);
  const pinnedShowScreenShare = useAppSelector((s) => s.mainUser.pinnedShowScreenShare);
  const scrolledRef = useRef<HTMLDivElement>(null);

  // 화면 공유 중인 메인 유저는 카메라 썸네일도 보여줘야 함
  const autoScreenSharing = mainUserId === localUserId
    ? screenShare.isSharing
    : mainUserId != null && (participants.participants[mainUserId]?.tracks.isScreenSharing ?? false);
  const mainUserIsScreenSharing = pinnedShowScreenShare !== null ? pinnedShowScreenShare : autoScreenSharing;

  // ─── 서브 유저 목록 구성 ──────────────────────────────────────────────────
  const subUsers = useMemo(() => {
    const list: ScreenUser[] = [];

    // 로컬 유저 — recorder 모드에서는 자기 자신(아바타)을 녹화 화면에 넣지 않는다.
    if (!isRecorderMode()) {
      // 로컬 유저 (메인이 아니면 포함, 또는 메인이지만 화면 공유 중이면 카메라 썸네일 포함)
      if (mainUserId !== localUserId || mainUserIsScreenSharing) {
        list.push({ id: localUserId, isLocal: true });
      }
      // 로컬 화면 공유 타일 (메인이 아닐 때, 또는 메인이지만 카메라가 메인일 때)
      if (screenShare.isSharing && (mainUserId !== localUserId || !mainUserIsScreenSharing)) {
        list.push({ id: localUserId, isLocal: true, isScreenShare: true });
      }
    }

    // 원격 참여자
    participants.participantIds.forEach((id) => {
      const p = participants.participants[id];
      if (!p) return;
      if (p.isHidden || p.properties?.features_jigasi) return;
      if (isRecordingBot(p)) return;
      const isMainUser = id === mainUserId;
      // 메인 유저: 공유화면이 메인이면 카메라를 서브에, 카메라가 메인이면 카메라 서브 제외
      if (isMainUser && !mainUserIsScreenSharing) {
        // 메인에 카메라가 표시 중 → 공유화면 타일만 서브에 추가
        if (p.tracks.isScreenSharing) {
          list.push({ id, isLocal: false, isScreenShare: true });
        }
        return;
      }
      list.push({ id, isLocal: false });
      if (p.tracks.isScreenSharing && !isMainUser) {
        list.push({ id, isLocal: false, isScreenShare: true });
      }
    });

    return list;
  }, [participants.participantIds, participants.participants, localUserId, mainUserId, mainUserIsScreenSharing, screenShare.isSharing]);

  // ─── 정렬 + 표시 제한 ───────────────────────────────────────────────────
  const sortedSubUsers = useMemo(() => {
    const users = [...subUsers];

    if (screenSort === 'SPEAKER' && speakerOrder.length > 0) {
      users.sort((a, b) => {
        const ai = speakerOrder.indexOf(a.id);
        const bi = speakerOrder.indexOf(b.id);
        const aSpeaker = ai !== -1 ? ai : Infinity;
        const bSpeaker = bi !== -1 ? bi : Infinity;
        return aSpeaker - bSpeaker;
      });
    } else if (screenSort === 'NAME') {
      const localItems = users.filter((u) => u.isLocal);
      const remoteItems = users.filter((u) => !u.isLocal);
      remoteItems.sort((a, b) => {
        const pA = participants.participants[a.id];
        const pB = participants.participants[b.id];
        const nameA = pA?.displayName || pA?.nickname || '';
        const nameB = pB?.displayName || pB?.nickname || '';
        return nameA.localeCompare(nameB, 'ko');
      });
      return [...localItems, ...remoteItems].slice(0, MAX_SIDE_SUB_COUNT);
    }

    return users.slice(0, MAX_SIDE_SUB_COUNT);
  }, [subUsers, screenSort, speakerOrder, participants.participants]);

  const handleClickSub = useCallback(
    (userId: string, showScreenShare?: boolean) => {
      dispatch(mainUserActions.pinUser({ userId, showScreenShare: showScreenShare ?? false }));
    },
    [dispatch],
  );

  if (sortedSubUsers.length === 0) return null;

  return (
    <div ref={scrolledRef} className="scrolled">
      {sortedSubUsers.map((user) => {
        const participant: Participant | 'local' = user.isLocal
          ? 'local'
          : participants.participants[user.id];
        if (!participant) return null;
        return (
          <VideoPanel
            key={user.isScreenShare ? `${user.id}-screen` : user.id}
            participant={participant}
            localUserId={localUserId}
            isMain={false}
            isThumbnail={true}
            onSelect={handleClickSub}
            showScreenShare={user.isScreenShare}
          />
        );
      })}
    </div>
  );
};

export default SideSubVideoList;
